import moment from "moment-timezone";
import React, { Component, Fragment, useState } from "react";
import PropTypes from "prop-types";
import { toJS } from "mobx";
import { inject, observer } from "mobx-react";
import _ from "lodash";
import postStore from "../../../../modules/social/approvePosts/UnapprovedPostsStore";
import { isImageOrVideo } from "../extensions";
import { MediaAttachment } from "../../../../components/uploadgallery/MediaAttachment";
import { NameChip } from "../../../../components";
import { canApprove, canWorkflow } from "../postActions/ToolbarActions";
import { EditScheduleModalTrigger } from "./EditSchedule";
import { UncontrolledTooltip } from "reactstrap";
import sessionStore from "../../../../services/session/SessionStore";
import userResolver from "../../../../services/users/UserResolver";
import { UserOrLoading } from "../../../../services/users/components/UserOrLoading";
import modalService from "../../../../components/modal/modalService";
import { ProviderImage } from "../../../../components";
import unapprovedPostsStore from "../../../../modules/social/approvePosts/UnapprovedPostsStore";
import { Confirm } from "../../../../components";
import { approvePost, retryPost, getDeliveryLogs, rejectPost } from "../api";
import { ToastService } from "../../../../components/toasts/Toasts";
import PostToBePublishedStatus from "../PostToBePublishedStatus";
import WorkflowSummaryLink from "../../../workflow/task/WorkflowSummaryLink";
import workflowApi from "../../../workflow/api";

import publishingCredentialStore from "../../../../modules/social/publishing/PublishingCredentialStore";
import socialPagesStore from "../../../../modules/social/publishing/SocialPagesStore";
import PostEditor from "../socialPostEditor/PostEditor";

export const renderDate = (date) => {
  let dateField = date;
  if (date && date.$date) {
    dateField = date.$date; // Handle inconsistent date formatting coming back from the server
  }

  return moment(dateField).tz(moment.tz.guess()).format("ddd, MMM D, h:mm a");
};

/**
 * This component is the 'details' view of a post which was scheduled for publishing.  It's currently displayed
 * in a modal from the publishing page in social.
 */
export const ScheduledPostCard = inject("scheduledPostStore")(observer(class ScheduledPostCard extends Component {
  static propTypes = {
    post: PropTypes.any, // A PostToBePublished
    hideControls: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      deliveryLogs: [],
      isRejecting: false,
      rejectedByAdmin: false,
      isSaving: false,
      rejectionText: "",
      workflowTask: null,
    };
  }

  disableNotes = true;
  componentDidMount() {
    const postId =
      typeof this.props.post._id === "object"
        ? this.props.post._id.$oid
        : this.props.post._id;
    getDeliveryLogs(postId).then((logs) => {
      this.setState({ deliveryLogs: logs });
    });

    workflowApi.getTaskByEntityId(postId).then((tasksResponse) => {
      if (!!tasksResponse.tasks && tasksResponse.tasks.length) {
        const workflowTask =
          tasksResponse.tasks[tasksResponse.tasks.length - 1];
        this.setState({ workflowTask: workflowTask });
      }
    });
  }

  onStartReject = () => {
    this.setState({ isRejecting: true });
  };

  onCancelReject = () => {
    this.setState({
      isRejecting: false,
      rejectionText: "",
      rejectedByAdmin: false,
    });
  };

  onSaveReject = () => {
    this.setState({ isSaving: true });
    rejectPost(this.props.post._id, this.state.rejectionText)
      .then((updatedPost) => {
        this.setState({
          isSaving: false,
          isRejecting: false,
          rejectionText: "",
          rejectedByAdmin: true,
        });
        ToastService.warning("Post rejected", "The post was not published.");
        return updatedPost;
      })
      .then(() => {
        // TODO #socialapproval - BAD mutating props
        // other code smells accessing various global state
        this.props.post.requiresApproval = true;
        this.props.post.rejectedForPublishing = true;
        modalService.closeModal();
      });
  };

  onApprove = () => {
    this.setState({ isSaving: true });

    const updatedPost = {
      ...this.props.post,
      requiresApproval: false,
      rejectedForPublishing: false,
      publishingStatus: PostToBePublishedStatus.Pending,
      approvedByUserId: sessionStore.user.userId,
    };
    const postId =
      typeof this.props.post._id === "object"
        ? this.props.post._id.$oid
        : this.props.post._id;
    return approvePost(postId, updatedPost)
      .then((updatedItem) => {
        this.setState({ isSaving: false });
        ToastService.success(
          "Post approved",
          "Your post is now queued or scheduled to be published."
        );
        return updatedItem;
      })
      .then(() => {
        this.props.post.requiresApproval = false;
        this.props.post.rejectedForPublishing = false;
        modalService.closeModal();
      });
  };

  renderApprovalStatus = () => {
    const { rejectedByAdmin } = this.state;
    const {
      hasBeenQueued,
      requiresApproval,
      rejectedForPublishing,
    } = this.props.post;
    const pendingApprovalPill = (
      <div className="badge badge-warning">Pending Approval</div>
    );
    const rejectedByAdminPill = (
      <div className="badge badge-danger">Rejected by Admin</div>
    );

    if (hasBeenQueued) return null;
    if (rejectedByAdmin) return rejectedByAdminPill;
    if (rejectedForPublishing) return rejectedByAdminPill;
    if (requiresApproval) return pendingApprovalPill;
    return null;
  };

  renderWorkflowLink = () => {
    if (!canWorkflow()) return undefined;
    if (!this.state.workflowTask) return undefined;
    return (
      <div className="ml-3 mr-3">
        <WorkflowSummaryLink workflowTask={this.state.workflowTask} />
      </div>
    );
  };

  render() {
    const { post } = this.props;

    function addNote(event) {
      let formData = new FormData(event.target);
      postStore.saveNote(post, { text: formData.get("text") });
      event.target.reset();
      event.stopPropagation();
      event.preventDefault();
    }

    return (
      <div className="scheduled-post-card">
        {this.renderTimeSummary()}
        <div className="card-body">
          <div className={"d-flex flex-row justify-content-between"}>
            <WhereItWasPosted event={post} logs={this.state.deliveryLogs} />
            <span>{this.renderApprovalStatus()}</span>
          </div>
          <div>
            <div className="card-spacer-top">
              <div className="flex-md" style={{ flexWrap: "wrap" }}>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  {post.details && (
                    <div className="clearfix m-3">
                      <PostEditor post={post} mode="view" />
                    </div>
                  )}
                  {this.props.hideControls
                    ? null
                    : !this.state.isRejecting
                    ? this.renderPostToolbar()
                    : this.renderPostRejection()}
                </div>
              </div>
            </div>

            {!this.disableNotes && (
              <div className="post-notes-container">
                {post.notes.map((note) => {
                  let author = userResolver.fetchOne(note.author);

                  return (
                    <div className="d-flex note" key={note._id.$oid}>
                      <div className="note-author">
                        <NameChip name={author.user.username} />
                      </div>
                      <div className="note-text">
                        <div className="author-details">
                          <UserOrLoading
                            className="username"
                            justName={true}
                            user={userResolver.fetchOne(note.author)}
                          />
                          <span className="separator">&bull;</span>
                          <span className="note-date">
                            {moment(note.dateAdded).fromNow()}
                          </span>
                        </div>
                        {note.text}
                      </div>
                    </div>
                  );
                })}

                <form
                  name="add-note-form"
                  className="form-inline"
                  onSubmit={addNote}
                >
                  <NameChip
                    name={sessionStore.user.username}
                    style={{ marginRight: "10px" }}
                  />
                  <input
                    type="text"
                    className="form-control"
                    name="text"
                    required
                  />
                  <button
                    type="submit"
                    className="btn btn-sm btn-secondary"
                    style={{ marginLeft: "5px" }}
                  >
                    Add Note
                  </button>
                </form>
              </div>
            )}

            {this.renderWorkflowLink()}
          </div>
        </div>
      </div>
    );
  }

  renderTimeSummary() {
    const { post } = this.props;
    const Icon = ({ icon = "clock" }) => (
      <span
        className={`ss ss-${icon} mr-2`}
        style={{ fontSize: "18px", verticalAlign: "middle" }}
      />
    );

    if (post.failure && post.failure.attemptsRemaining === 0) {
      const { failures } = post.failure;
      const lastFailure = failures[failures.length - 1];
      return (
        <div className="alert alert-danger m-0">
          <span>
            <Icon icon="delete" /> Post failed! Was scheduled for{" "}
            {renderDate(post.scheduledTime)}
            {ProcessFailure(lastFailure)}
          </span>
        </div>
      );
    } else if (post.countPosted === post.countQueued) {
      return (
        <div className="alert alert-success m-0">
          <span>
            <Icon icon="check" /> Published at {renderDate(post.completeTime)}
          </span>
        </div>
      );
    } else if (
      moment(post.scheduledTime).isSameOrBefore(
        moment().subtract(10, "minutes")
      )
    ) {
      return (
        <div className="alert alert-warning m-0">
          <Icon />
          Scheduled for {renderDate(post.scheduledTime)}{" "}
          <span className="font-weight-bold">(overdue)</span>
        </div>
      );
    } else {
      return (
        <div className="alert alert-warning m-0">
          <Icon />
          Scheduled for {renderDate(post.scheduledTime)}
        </div>
      );
    }
  }

  renderPostToolbar() {
    const { post, scheduledPostStore } = this.props;

    const deletePost = () => {
      modalService.requestModalV2({
        modal: (
          <Confirm
            visible={true}
            toggle={modalService.closeModal}
            header="Are you sure want to cancel this post?"
            message="Once deleted, the post cannot be recovered."
            confirmText="Delete"
            onContinue={() => {
              unapprovedPostsStore.deletePost(post).then(() => {
                ToastService.success("Post deleted.", "Success");
                scheduledPostStore.updateFilters({}); // aka, reload
              });
            }}
          />
        ),
      });
    };

    const renderButtonToolbar = () => {
      if (post.completeTime) {
        return null;
      } else {
        const renderApprovalControls =
          !post.rejectedForPublishing &&
          !this.state.rejectedByAdmin &&
          post.requiresApproval &&
          canApprove();

        return (
          <React.Fragment>
            {!post.hasBeenQueued && (
              <React.Fragment>
                <button
                  type="button"
                  className="btn btn-link text-secondary"
                  onClick={deletePost}
                  key="deletePost"
                >
                  <span className="fas fa-trash-alt" />
                </button>
                <EditScheduleModalTrigger
                  key="editScheduleModalTrigger"
                  edit={post}
                >
                  <button type="button" className="btn btn-link text-secondary">
                    <span className="fas fa-pencil" />
                  </button>
                </EditScheduleModalTrigger>
              </React.Fragment>
            )}
            {post.hasBeenQueued && (
              <div className="alert alert-success m-0">
                <span>
                  {" "}
                  This post is ready to be published at its scheduled time.{" "}
                </span>
              </div>
            )}
            {(renderApprovalControls || false) && (
              <React.Fragment>
                <button
                  disabled={this.state.isRejecting}
                  type="button"
                  className="btn btn-outline-secondary control-spacer-left"
                  onClick={this.onStartReject}
                >
                  Reject
                </button>
                <button
                  disabled={this.state.isSaving}
                  type="button"
                  className="btn btn-secondary control-spacer-left"
                  onClick={this.onApprove}
                >
                  Approve
                </button>
              </React.Fragment>
            )}
          </React.Fragment>
        );
      }
    };

    let userSummary = null;
    if (post.user) {
      userSummary = (
        <span>
          Posted by&nbsp;
          <strong
            id={"asdf"}
          >{`${post.user.firstName} ${post.user.lastName}`}</strong>
          <UncontrolledTooltip placement="bottom">
            {post.user.username}
            <br />
            {`${post.user.firstName} ${post.user.lastName}`}
            <br />
            {post.user.email}
          </UncontrolledTooltip>
        </span>
      );
    }

    return (
      <div className={`clearfix text-muted`}>
        {userSummary}
        <div className="pull-right">
          {post.updatePending && <span className="fas fa-spinner fa-spin" />}
        </div>
        <div>{renderButtonToolbar()}</div>
      </div>
    );
  }

  renderPostRejection = () => {
    return (
      <Fragment>
        <div className="row">
          <div className="form-group col">
            <label className="form-label">
              Reason for Rejection{" "}
              <span className="text-danger">(required)</span>
            </label>
            <br />
            <textarea
              className="form-control"
              rows={1}
              value={this.state.rejectionText}
              onChange={(e) => this.setState({ rejectionText: e.target.value })}
            ></textarea>
          </div>
        </div>
        <div className="row">
          <div className="form-group col">
            <button
              type="button"
              className="btn btn-outline-secondary mr-2"
              onClick={this.onCancelReject}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={this.onSaveReject}
              disabled={!this.state.rejectionText.trim().length}
            >
              Reject
            </button>
          </div>
        </div>
      </Fragment>
    );
  };
}));

const WhereItWasPosted = observer(({ event, logs, ...props }) => {
  const formatAddress = (listingPage) => {
    if (!listingPage.address) {
      return "";
    }
    const { address } = listingPage;
    const addressParts = [];

    if (address.streetAddress) addressParts.push(address.streetAddress);
    if (address.postalCode) addressParts.push(address.postalCode);
    return addressParts.join(", ");
  };

  /* Used to de-dupe remoteIds since publishing destinations can be groups and multiple groups can contain the same remote id.
   * Each remoteId gets exactly one publishing destination. */
  const generatePublishingDestinationMap = (postToBePublished) => {
    let publishingDestinationsByRemoteId = new Map();
    for (let destination of postToBePublished.publishTo) {
      for (let remoteId of destination.remoteIds) {
        if (!publishingDestinationsByRemoteId.has(remoteId)) {
          const adjustedDestination = { ...destination, remoteIds: [remoteId] };
          publishingDestinationsByRemoteId.set(remoteId, adjustedDestination);
        }
      }
    }
    return publishingDestinationsByRemoteId;
  };

  const renderShowRetry = (
    showRetry,
    postToBePublished,
    pageLogs,
    publishingDestination
  ) => {
    if (!showRetry) return undefined;
    return (
      <div className="social failure-container no-gutters">
        <div className="col-9">
          <span className="publishing-errors">
            {ProcessFailure(
              _.maxBy(pageLogs, (x) => x.dateAttempted.$date).error
            )}
          </span>
        </div>
        <div className="col-3">
          <RetryButton
            postId={postToBePublished._id}
            remoteId={publishingDestination.remoteIds[0]}
          />
        </div>
      </div>
    );
  };

  // Returns array with 2 elements: the jsx for the status badge and a boolean indicating if we should show the retry button.
  const renderStatusBadge = (pageLogs) => {
    // need to add in the colors here...
    const successLog = pageLogs.find((l) => l.success);
    if (successLog) {
      return [
        <i
          className="ss-checkcircle status-badge"
          title={`Published on ${renderDate(successLog.dateAttempted)}`}
        />,
        false,
      ];
    } else if (pageLogs.length > 0) {
      const lastFailure = _.maxBy(pageLogs, (x) => x.dateAttempted.$date);
      return [
        <i
          className="ss-deletecircle status-badge"
          title={`Publishing failed - ${lastFailure.error} - ${renderDate(
            lastFailure.$date
          )}`}
        />,
        true,
      ];
    } else {
      return [undefined, false];
    }
  };

  const renderUserPublishingDestination = (
    postToBePublished,
    publishingDestination,
    remoteId
  ) => {
    const publishTo = publishingCredentialStore.credentialById(
      postToBePublished.accountId,
      publishingDestination.credentialId
    );
    if (!publishTo) {
      console.info("unable to find account", toJS(publishingDestination));
      return; // might still be loading
    }

    // This middle case is compatibility for records in an older format
    const pageLogs =
      (publishingDestination.remoteIds.length > 0 &&
        logs.filter(
          (log) => log.remoteId === publishingDestination.remoteIds[0]
        )) ||
      (logs && [logs[0]]) ||
      [];
    const [statusBadge, showRetry] = renderStatusBadge(pageLogs);

    return [
      <li key={publishingDestination.credentialId}>
        <ProviderImage provider={event.provider} />
        <a href={publishTo.userDetails.link} target="_blank">
          {publishTo.remoteAccountName} {statusBadge}
        </a>
        {renderShowRetry(
          showRetry,
          postToBePublished,
          pageLogs,
          publishingDestination
        )}
      </li>,
    ];
  };

  const renderRegularPublishingDestination = (
    publishingDestination,
    postToBePublished
  ) => {
    return publishingDestination.remoteIds.map((remoteId) => {
      const publishTo = socialPagesStore.page(event.provider, remoteId);
      const pageLogs = logs.filter((log) => log.remoteId === remoteId);

      if (!publishTo) return undefined;
      const formattedAddress = formatAddress(publishTo);
      const [statusBadge, showRetry] = renderStatusBadge(pageLogs);

      return (
        <li key={remoteId}>
          <div className="social post-detail-destination">
            <div className="social provider-container mr-2 d-flex align-items-center">
              <ProviderImage provider={event.provider} />
            </div>
            <div className="social detail-container">
              <div>
                <a href={publishTo.url} target="_blank">
                  {publishTo.label} {statusBadge}
                </a>
              </div>
              {formattedAddress && (
                <div className="social address-container">
                  <span>{formattedAddress}</span>
                </div>
              )}
              {renderShowRetry(
                showRetry,
                postToBePublished,
                pageLogs,
                publishingDestination
              )}
            </div>
          </div>
        </li>
      );
    });
  };

  const renderPublishingDestinations = (postToBePublished) => {
    const publishingDestinationMap = generatePublishingDestinationMap(
      postToBePublished
    );
    const publishingDestinationsJSX = [];
    for (let remoteId of publishingDestinationMap.keys()) {
      const publishingDestination = publishingDestinationMap.get(remoteId);
      if (publishingDestination.pageType === "User") {
        publishingDestinationsJSX.push(
          renderUserPublishingDestination(
            postToBePublished,
            publishingDestination,
            remoteId
          )
        );
      } else {
        publishingDestinationsJSX.push(
          renderRegularPublishingDestination(
            publishingDestination,
            postToBePublished
          )
        );
      }
    }
    return publishingDestinationsJSX;
  };

  return (
    <ul className="publishing-list pl-0 mb-0">
      {renderPublishingDestinations(event)}
    </ul>
  );
}); // End WhereItWasPosted

const PostAttachment = ({ attachment }) => {
  if (!attachment) {
    return "";
  }
  if (
    attachment.attachmentType === "Image" ||
    attachment.attachmentType === "Video" ||
    isImageOrVideo(attachment.url)
  ) {
    return (
      <MediaAttachment
        src={attachment.url}
        imageStyle={{ width: "100%", maxWidth: "200px", maxHeight: "200px" }}
      />
    );
  } else if (attachment.attachmentType === "Link") {
    return (
      <a
        className="truncate"
        style={{ display: "block" }}
        href={attachment.url}
        target="_blank"
      >
        {attachment.url}
      </a>
    );
  } else {
    return "";
  }
};

const FailureReasons = {
  PostReplyingToNoLongerExists: "PostReplyingToNoLongerExists",
  SocialAccountNoLongerExists: "SocialAccountNoLongerExists",
  PageNoLongerExists: "PageNoLongerExists",
  AuthenticationFailure: "AuthenticationFailure",
  MissingPermission: "MissingPermission",
  ResolutionTooLarge: "ResolutionTooLarge",
  ResolutionTooSmall: "ResolutionTooSmall",
  VideoAspectRatioTooLarge: "VideoAspectRatioTooLarge",
  OtherMediaUploadFailure: "OtherMediaUploadFailure",
  StuckPending: "StuckPending",
  ProviderRejectedAttachment: "ProviderRejectedAttachment",
  UnrecoverableFailure: "UnrecoverableFailure",
  PublishingAuthenticationFailure: "PublishingAuthenticationFailure",
  ProviderUnreachable: "ProviderUnreachable",
  CommunityStandardsViolation: "CommunityStandardsViolation",
  TooManyHashtags: "TooManyHashtags",
  Forbidden: "Forbidden",
  ContactSupport: "ContactSupport",
  MediaDownloadTimeout: "MediaDownloadTimeout",
  ExpiredMedia: "ExpiredMedia",
  MediaCreationFailure: "MediaCreationFailure",
  UnknownUploadError: "UnknownUploadError",
  PossibleSpam: "PossibleSpam",
  DailyPublishingLimit: "DailyPublishingLimit",
  MediaNotFound: "MediaNotFound",
  MediaBuilderError: "MediaBuilderError",
  AccountRestricted: "AccountRestricted",
  UnsupportedVideoFormat: "UnsupportedVideoFormat",
  MediaCouldNotBeFetched: "MediaCouldNotBeFetched",
  MediaNotReadyForPublishing: "MediaNotReadyForPublishing",
  UnsupportedImageFormat: "UnsupportedImageFormat",
  ImageAspectRatioError: "ImageAspectRatioError",
  ImageCaptionTooLong: "ImageCaptionTooLong",
  Other: "Other",
};

export const ProcessFailure = (error) => {
  switch (error) {
    case FailureReasons.AuthenticationFailure:
      return (
        <div>
          <span>Authentication Failed</span> - update your credentials{" "}
          <a href="#/social/settings" onClick={() => modalService.closeModal()}>
            here
          </a>
          .
        </div>
      );
    case FailureReasons.MissingPermission:
      return (
        <div>
          Your account is missing the required permissions to use this feature. Please try
          re-authenticating and verifying that all permissions are enabled for the corresponding page(s).
        </div>
      );
    case FailureReasons.PublishingAuthenticationFailure:
      return (
        <div>
          <span>Publishing Authentication Failed</span> - please re-add
          publishing for the page(s){" "}
          <a href="#/social/settings" onClick={() => modalService.closeModal()}>
            here
          </a>
          .
        </div>
      );
    case FailureReasons.PostReplyingToNoLongerExists:
      return <div>The post your are replying to no longer exists</div>;
    case FailureReasons.SocialAccountNoLongerExists:
      return <div>The social account you are replying to no longer exists</div>;
    case FailureReasons.PageNoLongerExists:
      return <div>The page your are using no longer exists</div>;
    case FailureReasons.ResolutionTooLarge:
    case FailureReasons.ResolutionTooSmall:
      return (
        <div>
          The media resolution you posting is too $
          {failure.reason === FailureReasons.ResolutionTooLarge
            ? "large"
            : "small"}
        </div>
      );
    case FailureReasons.VideoAspectRatioTooLarge:
      return <div>The video aspect ratio is too large</div>;
    case FailureReasons.OtherMediaUploadFailure:
      return <div>The media failed to upload</div>;
    case FailureReasons.StuckPending:
      return (
        <div>
          The post is pending with the provider. At this time we can't determine
          if it is posted. Please wait a few minutes, and then check again as it
          may be posted.
        </div>
      );
    case FailureReasons.ProviderRejectedAttachment:
      return (
        <div>
          The attachment does not meet the provider's requirements. Please
          contact support for more details.
        </div>
      );
    case FailureReasons.ProviderUnreachable:
      return (
        <div>
          Due to a temporary issue at the provider, we are unable to publish
          your post. Please wait while we attempt to post again, or try again
          later.
        </div>
      );
    case FailureReasons.CommunityStandardsViolation:
    case FailureReasons.PossibleSpam:
      return (
        <div>
          Your post has been declined by Instagram for going against their
          Community Standards. In an effort to prevent their Community Standards
          being circumvented, they have not advised what part of the content has
          breached their Community Standards. We recommend amending the post in
          terms of the text and/or image and trying to re-publish your post. For
          more information on Instagram’s Community Standards, please see this
          page:{" "}
          <a href="https://help.instagram.com/477434105621119" target="_blank">
            https://help.instagram.com/477434105621119
          </a>
          .
        </div>
      );
    case FailureReasons.Forbidden:
      return (
        <div>
          The provider forbid us to publish to this location with your social
          credentials. Please check the status of this location with the
          provider and try again.
        </div>
      );
    case FailureReasons.TooManyHashtags:
      return (
        <div>
          {" "}
          Your post has been declined by Instagram for having more than 30
          hashtags.
        </div>
      );
    case FailureReasons.ContactSupport:
      return (
        <div>
          {" "}
          We were unable to successfully post this post. Please contact your
          account manager for more details.
        </div>
      );
    case FailureReasons.MediaDownloadTimeout:
      return <div>A timeout occurred while downloading the media. Try again.</div>;
    case FailureReasons.ExpiredMedia:
    case FailureReasons.MediaCreationFailure:
    case FailureReasons.MediaNotFound:
    case FailureReasons.MediaBuilderError:
    case FailureReasons.MediaCouldNotBeFetched:
    case FailureReasons.UnknownUploadError:
    case FailureReasons.MediaNotReadyForPublishing:
      return <div>There was a problem handling your media. Re-add the media and try again.</div>;
    case FailureReasons.DailyPublishingLimit:
      return <div>You've reached the daily publishing limit for this provider. Please try again on another day.</div>;
    case FailureReasons.AccountRestricted:
      return (
        <div>
          The account used to publish to this provider is inactive or restricted. Please sign in to the provider's website
          and complete any action steps required to re-enable the account and try again.
        </div>
      );
    case FailureReasons.UnsupportedVideoFormat:
      return <div>The video format is not supported.</div>;
    case FailureReasons.UnsupportedImageFormat:
      return <div>There's a possible permission error with your image. Re-upload it and try again.</div>;
    case FailureReasons.ImageAspectRatioError:
      return <div>The image's aspect ratio cannot be accepted for publishing.</div>;
    case FailureReasons.ImageCaptionTooLong:
      return <div>The submitted image's caption was too long.</div>;
    case FailureReasons.Other:
    default:
      return <div>An unknown error occurred. Please contact support.</div>;
  }
};

const RetryButton = ({ postId, remoteId }) => {
  const [isLoading, setLoading] = useState(false);
  const [isQueued, setQueued] = useState(false);

  const onClicked = () => {
    if (!isLoading) {
      setLoading(true);
      retryPost({ postId, remoteId }).then((done) => {
        setQueued(true);
        setLoading(false);
      });
    }
  };

  if (isQueued) {
    return <span>pending</span>;
  } else {
    return (
      <a
        style={{
          textDecoration: "underline",
          cursor: isLoading ? "auto" : "pointer",
          display: "inline-block",
        }}
        onClick={onClicked}
      >
        {isLoading && <i className="fas fa-spinner fa-spin" />}{" "}
        {isLoading ? "" : "Retry"}
      </a>
    );
  }
};
